'use client';

import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

import { TopProgressBar } from '@/components/ui/TopProgressBar/TopProgressBar';

type TopProgressBarContextValue = {
  isLoading: boolean;
  start: () => void;
  stop: () => void;
  track: <T>(promiseFactory: () => Promise<T>) => Promise<T>;
};

const TopProgressBarContext = createContext<
  TopProgressBarContextValue | undefined
>(undefined);

export const useTopProgressBar = () => {
  const context = useContext(TopProgressBarContext);

  if (!context) {
    throw new Error(
      'useTopProgressBar must be used within TopProgressBarProvider'
    );
  }

  return context;
};

export const TopProgressBarProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const [pendingCount, setPendingCount] = useState(0);

  const start = useCallback(() => {
    setPendingCount(prev => prev + 1);
  }, []);

  const stop = useCallback(() => {
    setPendingCount(prev => Math.max(0, prev - 1));
  }, []);

  const track = useCallback(
    async <T,>(promiseFactory: () => Promise<T>): Promise<T> => {
      start();

      try {
        return await promiseFactory();
      } finally {
        stop();
      }
    },
    [start, stop]
  );

  const value = useMemo(
    () => ({
      isLoading: pendingCount > 0,
      start,
      stop,
      track
    }),
    [pendingCount, start, stop, track]
  );

  return (
    <TopProgressBarContext.Provider value={value}>
      <TopProgressBar isAnimating={pendingCount > 0} />
      {children}
    </TopProgressBarContext.Provider>
  );
};
